import {
  differenceInCalendarDays,
  format,
  formatDistanceToNow,
  isValid,
  parseISO,
} from 'date-fns'

export const formatDate = (dateString, options) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', options)
}

/** MM-DD-YYYY for forms, actuals, and PDF output */
export const formatDateMdY = (date) => {
  if (!date) return ''
  const d = new Date(new Date(date).getTime())
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  let year = String(d.getFullYear())
  while (year.length < 4) year = '0' + year
  return [month, day, year].join('-')
}

/** YYYY-MM-DD with legacy timezone offset handling for table displays */
export const formatDateYmd = (date) => {
  if (!date) return ''
  const today = new Date(date)
  const d = new Date(
    today.getTime() + Math.abs(today.getTimezoneOffset() * 60000),
  )
  const month = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  let year = String(d.getFullYear())
  while (year.length < 4) year = '0' + year
  return [year, month, day].join('-')
}

/** M/D/YYYY for compact card displays */
export const formatDateNumeric = (dateString) => {
  const date = new Date(dateString)
  return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

const toDate = (value) => {
  if (!value && value !== 0) return null
  if (value instanceof Date) return value
  if (typeof value === 'number') return new Date(value)
  if (typeof value === 'string') {
    const parsed = value.includes('T')
      ? parseISO(value)
      : new Date(value)
    return parsed
  }
  return new Date(value)
}

const safeFormat = (value, pattern) => {
  const date = toDate(value)
  if (!date || !isValid(date)) return ''
  return format(date, pattern)
}

/** Former moment format: MMM Do YYYY → e.g. Jan 5th 2024 */
export const formatDisplayDate = (value) =>
  safeFormat(value, 'MMM do yyyy')

/** Former moment format: MMM D, YYYY */
export const formatShortDate = (value) =>
  safeFormat(value, 'MMM d, yyyy')

/** Former moment format: MMM YYYY */
export const formatMonthYear = (value) =>
  safeFormat(value, 'MMM yyyy')

/** Former moment format: MMMM YYYY */
export const formatMonthYearLong = (value) =>
  safeFormat(value, 'MMMM yyyy')

/** Calendar-day delta from `from` to `to` (default now). */
export const daysBetween = (from, to = new Date()) => {
  const start = toDate(from)
  const end = toDate(to)
  if (!start || !end || !isValid(start) || !isValid(end)) return 0
  return differenceInCalendarDays(end, start)
}

/** Absolute calendar days between two dates (default end = now). */
export const absoluteDaysBetween = (from, to = new Date()) =>
  Math.abs(daysBetween(from, to))

/** Rounded absolute days — common "X days ago" / "days on market" pattern. */
export const roundedDaysBetween = (from, to = new Date()) =>
  Math.round(absoluteDaysBetween(from, to))

/** Former moment(...).fromNow() */
export const fromNow = (value) => {
  const date = toDate(value)
  if (!date || !isValid(date)) return ''
  return formatDistanceToNow(date, { addSuffix: true  })
}
